import { useState, useRef, useEffect } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import { useTheme } from './ThemeContext';
import SearchBar from './SearchBar';
import anthroperLogo from '../logo-anthroper.svg';
import API_BASE_URL from '../config';
import features from '../features';

function SunIcon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5" /><line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" /><line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" /><line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" /><line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" /></svg>);
}
function MoonIcon() {
  return (<svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" /></svg>);
}
function ChevronIcon() {
  return (<svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="6 9 12 15 18 9" /></svg>);
}

var dropdownCategories = [
  { label: 'Finance', items: [
    { path: '/loan-calculator', label: 'Loan Calculator' },
    { path: '/salary-calculator', label: 'Salary Calculator' },
    { path: '/sip-calculator', label: 'SIP Calculator' },
    { path: '/roi-calculator', label: 'ROI Calculator' },
  ]},
  { label: 'Developer', items: [
    { path: '/json-formatter', label: 'JSON Formatter' },
    { path: '/base64', label: 'Base64 Encode/Decode' },
    { path: '/uuid-generator', label: 'UUID Generator' },
    { path: '/timestamp', label: 'Timestamp Converter' },
    { path: '/csv-tools', label: 'CSV Tools' },
  ]},
  { label: 'Text', items: [
    { path: '/markdown-preview', label: 'Markdown Preview' },
    { path: '/word-counter', label: 'Word Counter' },
    { path: '/case-converter', label: 'Case Converter' },
    { path: '/lorem-generator', label: 'Lorem Ipsum' },
  ]},
  { label: 'Security', items: [
    { path: '/password-generator', label: 'Password Generator' },
    { path: '/hash-generator', label: 'Hash Generator' },
  ]},
  { label: 'Design & SEO', items: [
    { path: '/color-converter', label: 'Color Converter' },
    { path: '/seo-checker', label: 'SEO Checker' },
  ]},
];

function Layout() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [agentUrl, setAgentUrl] = useState(null);
  const [user, setUser] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  var dropdownRef = useRef(null);
  var userMenuRef = useRef(null);
  var hoverTimeout = useRef(null);
  var closeMenu = function () { setMenuOpen(false); setToolsOpen(false); };

  useEffect(function() {
    if (!features.agentEnabled) return;
    var token = localStorage.getItem('agent_token');
    var stored = localStorage.getItem('agent_user');
    if (!token || !stored) return;
    try { setUser(JSON.parse(stored)); } catch(e) {}
    fetch(API_BASE_URL + '/api/projects', { headers: { Authorization: 'Bearer ' + token } })
      .then(function(r) { return r.json(); })
      .then(function(d) {
        if (d.projects && d.projects.length > 0) setAgentUrl('/agent/dashboard/' + d.projects[0].id);
        else setAgentUrl('/agent/onboarding');
      }).catch(function() {});
  }, []);

  useEffect(function() {
    function handleClick(e) {
      if (userMenuRef.current && !userMenuRef.current.contains(e.target)) setUserMenuOpen(false);
    }
    document.addEventListener('mousedown', handleClick);
    return function() { document.removeEventListener('mousedown', handleClick); };
  }, []);

  function handleSignOut() {
    localStorage.removeItem('agent_token');
    localStorage.removeItem('agent_user');
    setUser(null); setAgentUrl(null); setUserMenuOpen(false);
    navigate('/');
  }

  var isMobile = function () { return window.innerWidth <= 768; };

  var handleToolsMouseEnter = function () {
    if (isMobile()) return;
    clearTimeout(hoverTimeout.current);
    setToolsOpen(true);
  };
  var handleToolsMouseLeave = function () {
    if (isMobile()) return;
    hoverTimeout.current = setTimeout(function () { setToolsOpen(false); }, 200);
  };
  var handleToolsClick = function (e) {
    if (isMobile()) {
      e.preventDefault();
      setToolsOpen(!toolsOpen);
    } else {
      closeMenu();
      navigate('/tools');
    }
  };

  useEffect(function () {
    function handleClickOutside(e) { if (dropdownRef.current && !dropdownRef.current.contains(e.target)) setToolsOpen(false); }
    document.addEventListener('mousedown', handleClickOutside);
    return function () { document.removeEventListener('mousedown', handleClickOutside); clearTimeout(hoverTimeout.current); };
  }, []);

  return (
    <div className="site-wrapper">
      <nav className="navbar" role="navigation" aria-label="Main navigation">
        <div className="nav-container">
          <Link to="/" className="nav-logo" onClick={closeMenu}><img src={anthroperLogo} alt="Anthroper" className="nav-logo-img" /> Anthroper</Link>
          <ul className={"nav-links" + (menuOpen ? " open" : "")}>
            <li><NavLink to="/" end onClick={closeMenu}>Home</NavLink></li>
            <li><NavLink to="/blog" onClick={closeMenu}>Articles</NavLink></li>
            <li className="nav-dropdown" ref={dropdownRef} onMouseEnter={handleToolsMouseEnter} onMouseLeave={handleToolsMouseLeave}>
              <button className={"nav-dropdown-trigger" + (toolsOpen ? " active" : "")} onClick={handleToolsClick} type="button" aria-expanded={toolsOpen} aria-haspopup="true">Tools <ChevronIcon /></button>
              <div className={"dropdown-menu dropdown-mega" + (toolsOpen ? " open" : "")}>
                {dropdownCategories.map(function (cat) {
                  return (
                    <div key={cat.label} className="dropdown-col">
                      <span className="dropdown-cat-label">{cat.label}</span>
                      <ul>
                        {cat.items.map(function (t) { return <li key={t.path}><NavLink to={t.path} onClick={closeMenu}>{t.label}</NavLink></li>; })}
                      </ul>
                    </div>
                  );
                })}
              </div>
            </li>
            <li><NavLink to="/learn" onClick={closeMenu}>Learn</NavLink></li>
            <li><NavLink to="/products" onClick={closeMenu}>Products</NavLink></li>
          </ul>
          <div className={"nav-actions" + (menuOpen ? " menu-open" : "")}>
            <SearchBar />
            <button className="theme-toggle" onClick={toggleTheme} title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"} type="button">{theme === "dark" ? <SunIcon /> : <MoonIcon />}</button>
            {features.agentEnabled && (user ? (
              <div className="nav-user" ref={userMenuRef}>
                <button className="nav-user-btn" onClick={function() { setUserMenuOpen(!userMenuOpen); }} aria-label="User menu" type="button">
                  <span className="nav-user-avatar">{(user.name || user.email).charAt(0).toUpperCase()}</span>
                  <span className="nav-user-name">{user.name || user.email.split('@')[0]}</span>
                  <ChevronIcon />
                </button>
                {userMenuOpen && (
                  <div className="nav-user-menu">
                    <div className="nav-user-menu-header">
                      <span className="nav-user-menu-name">{user.name || 'Account'}</span>
                      <span className="nav-user-menu-email">{user.email}</span>
                    </div>
                    {agentUrl && <Link to={agentUrl} className="nav-user-menu-item" onClick={function() { setUserMenuOpen(false); }}>Dashboard</Link>}
                    <button type="button" className="nav-user-menu-item nav-user-menu-item--danger" onClick={handleSignOut}>Sign Out</button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/signin" className="btn btn-primary" style={{ fontSize: '0.82rem', padding: '0.4rem 1rem' }}>Sign In</Link>
            ))}
            <button className={"nav-toggle" + (menuOpen ? " active" : "")} onClick={function () { setMenuOpen(!menuOpen); }} aria-label="Toggle navigation menu" aria-expanded={menuOpen} type="button"><span></span><span></span><span></span></button>
          </div>
        </div>
      </nav>
      <main className="site-main"><Outlet /></main>
      <footer className="site-footer">
        <div className="footer-container">
          <div className="footer-grid">
            <div className="footer-col">
              <h3><img src={anthroperLogo} alt="" className="footer-logo-img" /> Anthroper</h3>
              <p>Articles, insights, and free online tools for developers, writers, and everyone. Stay informed and work smarter.</p>
            </div>
            <div className="footer-col">
              <h4>Content</h4>
              <ul>
                <li><Link to="/blog">Articles</Link></li>
                <li><Link to="/learn">Learn</Link></li>
                <li><Link to="/products">Products</Link></li>
                <li><Link to="/tools">All Tools</Link></li>
              </ul>
              <h4 style={{ marginTop: '1rem' }}>Company</h4>
              <ul>
                <li><Link to="/about">About Us</Link></li>
                <li><Link to="/contact">Contact</Link></li>
                <li><Link to="/privacy-policy">Privacy Policy</Link></li>
                <li><Link to="/terms">Terms & Conditions</Link></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>Popular Tools</h4>
              <ul>
                <li><Link to="/json-formatter">JSON Formatter</Link></li>
                <li><Link to="/password-generator">Password Generator</Link></li>
                <li><Link to="/loan-calculator">Loan Calculator</Link></li>
                <li><Link to="/word-counter">Word Counter</Link></li>
                <li><Link to="/base64">Base64 Encode/Decode</Link></li>
                <li><Link to="/color-converter">Color Converter</Link></li>
              </ul>
            </div>
            <div className="footer-col">
              <h4>More Tools</h4>
              <ul>
                <li><Link to="/salary-calculator">Salary Calculator</Link></li>
                <li><Link to="/sip-calculator">SIP Calculator</Link></li>
                <li><Link to="/roi-calculator">ROI Calculator</Link></li>
                <li><Link to="/csv-tools">CSV Tools</Link></li>
                <li><Link to="/case-converter">Case Converter</Link></li>
                <li><Link to="/uuid-generator">UUID Generator</Link></li>
                <li><Link to="/timestamp">Timestamp Converter</Link></li>
                <li><Link to="/seo-checker">SEO Checker</Link></li>
              </ul>
            </div>
          </div>
          <div className="footer-bottom">
            <p>{"\u00A9"} {new Date().getFullYear()} Anthroper. All rights reserved.</p>
            <p>Articles and insights for everyone. Tools run in your browser — no data is sent to any server.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default Layout;
